import { Component, OnInit, AfterViewInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { SessionService } from 'src/app/services/session.service';
import { UserService } from 'src/app/services/user.service';
import { CrmClientService } from 'src/app/services/crm-client.service';
import { CrmBrandService } from 'src/app/services/crm-brand.service';
import { CrmDataSourceService } from 'src/app/services/crm-data-source.service';
import { CrmBrandSourceXrefService } from 'src/app/services/crm-brand-source-xref.service';
import { CrmCallService } from 'src/app/services/crm-call.service';
import { CrmHolidayService } from 'src/app/services/crm-holiday.service';

@Component({
  selector: 'app-crm-portal',
  templateUrl: './crm-portal.component.html',
  styleUrls: ['./crm-portal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CrmPortalComponent implements OnInit, AfterViewInit, OnDestroy {
  isDarkMode: boolean = false;
  username: string = '';
  userDisplayLetter: string = 'U';
  initialScreen: string = 'dashboard';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private userService: UserService,
    private crmClientService: CrmClientService,
    private crmBrandService: CrmBrandService,
    private crmDataSourceService: CrmDataSourceService,
    private crmBrandSourceXrefService: CrmBrandSourceXrefService,
    private crmCallService: CrmCallService,
    private crmHolidayService: CrmHolidayService
  ) {
    this.username = this.sessionService.getUsername() || 'User';
    this.userDisplayLetter = typeof this.username === 'string' && this.username.length > 0 ? this.username.charAt(0).toUpperCase() : 'U';

    const crmUserName = this.sessionService.getCrmUserName();
    const emailLocalPart = typeof this.username === 'string' ? this.username.split('@')[0].split('.')[0] : '';
    const fallbackName = emailLocalPart ? emailLocalPart.charAt(0).toUpperCase() + emailLocalPart.slice(1) : 'there';
    (window as any).CRM_REAL_NAME = crmUserName || fallbackName;
  }

  ngOnInit(): void {
    document.documentElement.setAttribute('data-theme', 'light');
    document.body.setAttribute('data-theme', 'light');

    const crmAccess = this.sessionService.getCrmAccess();
    if (crmAccess === 'N') {
      this.router.navigate(['home']);
      return;
    }

    // Read screen from route parameter e.g. /crm-portal/clients
    const routeScreen = this.route.snapshot.paramMap.get('screen');
    if (routeScreen) {
      this.initialScreen = routeScreen;
    }

    (window as any).saveCrmClientApi = (clientPayload: any, isEdit: boolean, clientId?: number) => {
      this.saveCrmClientApi(clientPayload, isEdit, clientId);
    };

    (window as any).loadCrmClientsFromApi = () => {
      this.loadClients();
    };

    (window as any).saveCrmBrandApi = (brandPayload: any, isEdit: boolean, brandId?: number) => {
      this.saveCrmBrandApi(brandPayload, isEdit, brandId);
    };

    (window as any).loadCrmBrandsFromApi = (clientKey?: number) => {
      this.loadBrands(clientKey);
    };

    (window as any).saveCrmDataSourceApi = (dataSourcePayload: any, isEdit: boolean, dataSourceId?: number, onDone?: (result: any) => void) => {
      this.saveCrmDataSourceApi(dataSourcePayload, isEdit, dataSourceId, onDone);
    };

    (window as any).loadCrmDataSourcesFromApi = () => {
      this.loadDataSources();
    };

    (window as any).saveCrmBrandSourceXrefApi = (xrefPayload: any) => {
      this.saveCrmBrandSourceXrefApi(xrefPayload);
    };

    (window as any).loadCrmBrandSourceXrefsFromApi = (clientKey?: number, brandKey?: number) => {
      this.loadBrandSourceXrefs(clientKey, brandKey);
    };

    (window as any).deleteCrmBrandSourceXrefApi = (xrefId: number) => {
      this.deleteCrmBrandSourceXrefApi(xrefId);
    };

    (window as any).saveCrmCallApi = (callPayload: any, isEdit: boolean, eventId?: string) => {
      this.saveCrmCallApi(callPayload, isEdit, eventId);
    };

    (window as any).loadCrmCallsFromApi = () => {
      this.loadCalls();
    };

    (window as any).cancelCrmCallApi = (eventId: string) => {
      this.cancelCrmCallApi(eventId);
    };

    (window as any).loadCrmCallUsersFromApi = () => {
      this.loadCrmCallUsers();
    };

    (window as any).loadCrmAllBrandsFromApi = () => {
      this.loadAllBrandsForCalls();
    };

    (window as any).loadCrmHolidaysFromApi = () => {
      this.loadHolidays();
    };

    (window as any).saveCrmHolidayApi = (holidayPayload: any, isEdit: boolean, holidayId?: number) => {
      this.saveCrmHolidayApi(holidayPayload, isEdit, holidayId);
    };

    (window as any).deleteCrmHolidayApi = (holidayId: number) => {
      this.deleteCrmHolidayApi(holidayId);
    };

    (window as any).navigateCrmRoute = (screen: string) => {
      if (screen) {
        this.router.navigate(['/crm-portal', screen], { replaceUrl: true });
      }
    };
  }

  ngAfterViewInit(): void {
    // Remove previous script instance if exists
    const existingScript = document.getElementById('crm-portal-dynamic-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Dynamically inject raw CRM engine script
    const script = document.createElement('script');
    script.id = 'crm-portal-dynamic-script';
    script.src = 'assets/js/crm-portal-script.js?v=' + new Date().getTime();
    script.async = true;
    script.onload = () => {
      console.log('DU SPL Portal Script Loaded & Initialized Successfully!');
      this.loadClients();
      if (typeof (window as any).unifiedGo === 'function') {
        (window as any).unifiedGo(this.initialScreen);
      }
    };
    document.body.appendChild(script);
  }

  loadClients(): void {
    console.log('Fetching CRM clients from API...');
    this.crmClientService.getClients().subscribe({
      next: (data: any[]) => {
        console.log('CRM Clients API Response Received:', data);
        if (Array.isArray(data) && data.length > 0) {
          const mappedClients = data.map(c => {
            const clientId = c.ClientId ?? c.clientId ?? c.id;
            const clientKey = c.ClientKey ?? c.clientKey;
            const clientName = c.ClientName ?? c.clientName ?? c.CompanyName ?? c.companyName ?? c.name ?? 'Client';
            const companyName = c.CompanyName ?? c.companyName ?? c.company ?? clientName;
            const clientContact = c.ClientContact ?? c.clientContact ?? c.contact ?? '';
            const clientEmail = c.ClientEmail ?? c.clientEmail ?? c.email ?? '';
            const onshoreLead = c.OnshoreLead ?? c.onshoreLead ?? '';
            const opsLead = c.OpsLead ?? c.opsLead ?? '';
            const splLead = c.SplLead ?? c.splLead ?? c.spl ?? '';
            const brandColor = c.BrandColor ?? c.brandColor ?? c.color ?? '#6366F1';
            const logoUrl = c.LogoUrl ?? c.logoUrl ?? c.logo ?? '';
            const startDate = c.StartDate ?? c.startDate ?? c.start ?? '';
            const goLiveDate = c.GoLiveDate ?? c.goLiveDate ?? c.golive ?? '';
            const status = c.Status ?? c.status ?? 'Active';

            return {
              id: clientId,
              clientKey: clientKey,
              name: clientName,
              company: companyName,
              contact: clientContact,
              email: clientEmail,
              onshoreLead: onshoreLead,
              opsLead: opsLead,
              spl: splLead,
              color: brandColor,
              logo: logoUrl,
              start: typeof startDate === 'string' && startDate.length >= 10 ? startDate.substring(0, 10) : '',
              golive: typeof goLiveDate === 'string' && goLiveDate.length >= 10 ? goLiveDate.substring(0, 10) : '',
              statusLabel: status,
              active: status === 'Active'
            };
          });

          (window as any).DB_CLIENTS_DATA = mappedClients;
          if (typeof (window as any).updateClientsFromApi === 'function') {
            (window as any).updateClientsFromApi(mappedClients);
          }
          if (this.initialScreen === 'brands' || (window as any).STATE?.screen === 'brands') {
            const activeClientKey = mappedClients[0].clientKey || mappedClients[0].id;
            this.loadBrands(activeClientKey);
          }
        }
      },
      error: (err) => {
        console.error('Failed to load CRM clients from API:', err);
      }
    });
  }

  saveCrmClientApi(clientPayload: any, isEdit: boolean, clientId?: number): void {
    const userEmail = this.username || 'system';
    if (isEdit && clientId) {
      this.crmClientService.updateClient(clientId, clientPayload, userEmail).subscribe({
        next: (res) => {
          this.loadClients();
        },
        error: (err) => console.error('Error updating CRM client:', err)
      });
    } else {
      this.crmClientService.createClient(clientPayload, userEmail).subscribe({
        next: (res) => {
          this.loadClients();
        },
        error: (err) => console.error('Error creating CRM client:', err)
      });
    }
  }

  loadBrands(clientKey?: number): void {
    if (!clientKey) {
      if (typeof (window as any).CC === 'function') {
        try {
          const curClient = (window as any).CC();
          if (curClient && curClient.clientKey) {
            clientKey = curClient.clientKey;
          }
        } catch(e) {}
      }
      if (!clientKey && (window as any).DB_CLIENTS_DATA && (window as any).DB_CLIENTS_DATA.length > 0) {
        clientKey = (window as any).DB_CLIENTS_DATA[0].clientKey || (window as any).DB_CLIENTS_DATA[0].id;
      }
    }
    console.log('Fetching CRM brands from API for clientKey:', clientKey);
    this.crmBrandService.getBrands(clientKey).subscribe({
      next: (data: any[]) => {
        console.log('CRM Brands API Response Received:', data);
        if (Array.isArray(data)) {
          const mappedBrands = data.map(b => {
            const brandId = b.BrandId ?? b.brandId ?? b.id;
            const brandKey = b.BrandKey ?? b.brandKey;
            const brandName = b.BrandName ?? b.brandName ?? b.name ?? 'Brand';
            const status = b.Status ?? b.status ?? 'Active';
            const area = b.TherapeuticArea ?? b.therapeuticArea ?? b.area ?? '';
            const liveDate = b.DuLiveDate ?? b.duLiveDate ?? b.live ?? '';
            const decommDate = b.DecommissionDate ?? b.decommissionDate ?? b.decomm ?? '';
            const contact = b.BrandContact ?? b.brandContact ?? '';
            const onshore = b.OnshoreLead ?? b.onshoreLead ?? '';
            const ops = b.OpsLead ?? b.opsLead ?? '';
            const spl = b.SplLead ?? b.splLead ?? '';
            const cKey = b.ClientKey ?? b.clientKey;
            const cName = b.ClientName ?? b.clientName ?? '';

            return {
              id: brandId,
              brandKey: brandKey,
              clientId: cKey,
              client_id: cKey,
              clientKey: cKey,
              clientName: cName,
              name: brandName,
              status: status,
              area: area,
              live: typeof liveDate === 'string' && liveDate.length >= 10 ? liveDate.substring(0, 10) : liveDate,
              decomm: typeof decommDate === 'string' && decommDate.length >= 10 ? decommDate.substring(0, 10) : decommDate,
              brandContact: contact,
              onshoreLead: onshore,
              opsLead: ops,
              splLead: spl,
              spl: spl
            };
          });

          (window as any).DB_BRANDS_DATA = mappedBrands;
          if (typeof (window as any).updateBrandsFromApi === 'function') {
            (window as any).updateBrandsFromApi(mappedBrands);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM brands from API:', err)
    });
  }

  // Call Schedule isn't scoped to the header client, so its Brand picker needs the
  // FULL brand catalog -- loadBrands() infers the header's current client when no
  // clientKey is passed, which would silently narrow this list, so this bypasses
  // that inference and always asks the API for every brand (clientKey: null).
  loadAllBrandsForCalls(): void {
    this.crmBrandService.getBrands(undefined).subscribe({
      next: (data: any[]) => {
        if (Array.isArray(data)) {
          const mapped = data.map(b => ({
            brandKey: b.BrandKey ?? b.brandKey,
            name: b.BrandName ?? b.brandName ?? b.name ?? 'Brand',
            clientKey: b.ClientKey ?? b.clientKey,
            clientName: b.ClientName ?? b.clientName ?? ''
          }));
          if (typeof (window as any).updateCrmAllBrandsFromApi === 'function') {
            (window as any).updateCrmAllBrandsFromApi(mapped);
          }
        }
      },
      error: (err) => console.error('Failed to load all CRM brands for Call Schedule:', err)
    });
  }

  saveCrmBrandApi(brandPayload: any, isEdit: boolean, brandId?: number): void {
    const userEmail = this.username || 'system';
    if (isEdit && brandId) {
      this.crmBrandService.updateBrand(brandId, brandPayload, userEmail).subscribe({
        next: (res) => {
          this.loadBrands();
        },
        error: (err) => console.error('Error updating CRM brand:', err)
      });
    } else {
      this.crmBrandService.createBrand(brandPayload, userEmail).subscribe({
        next: (res) => {
          this.loadBrands();
        },
        error: (err) => console.error('Error creating CRM brand:', err)
      });
    }
  }

  loadDataSources(): void {
    console.log('Fetching CRM data sources from API...');
    this.crmDataSourceService.getDataSources().subscribe({
      next: (data: any[]) => {
        console.log('CRM Data Sources API Response Received:', data);
        if (Array.isArray(data)) {
          const mappedDataSources = data.map(d => {
            const dsKey = d.DsKey ?? d.dsKey ?? d.id;
            const dsName = d.DsName ?? d.dsName ?? 'Data Source';
            const dsType = d.DsType ?? d.dsType ?? '';
            const dsAltKey = d.DsAltKey ?? d.dsAltKey ?? null;
            const dsAltName = d.DsAltName ?? d.dsAltName ?? '';

            return {
              id: dsKey,
              dsKey: dsKey,
              name: dsName,
              type: dsType,
              altKey: dsAltKey,
              altName: dsAltName
            };
          });

          (window as any).DB_DATA_SOURCES_DATA = mappedDataSources;
          if (typeof (window as any).updateDataSourcesFromApi === 'function') {
            (window as any).updateDataSourcesFromApi(mappedDataSources);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM data sources from API:', err)
    });
  }

  saveCrmDataSourceApi(dataSourcePayload: any, isEdit: boolean, dataSourceId?: number, onDone?: (result: any) => void): void {
    const userEmail = this.username || 'system';
    if (isEdit && dataSourceId) {
      this.crmDataSourceService.updateDataSource(dataSourceId, dataSourcePayload, userEmail).subscribe({
        next: (res) => {
          this.loadDataSources();
          if (onDone) { onDone(res); }
        },
        error: (err) => console.error('Error updating CRM data source:', err)
      });
    } else {
      this.crmDataSourceService.createDataSource(dataSourcePayload, userEmail).subscribe({
        next: (res) => {
          this.loadDataSources();
          if (onDone) { onDone(res); }
        },
        error: (err) => console.error('Error creating CRM data source:', err)
      });
    }
  }

  loadBrandSourceXrefs(clientKey?: number, brandKey?: number): void {
    if (!clientKey && typeof (window as any).CC === 'function') {
      try {
        const curClient = (window as any).CC();
        if (curClient && curClient.clientKey) {
          clientKey = curClient.clientKey;
        }
      } catch(e) {}
    }
    console.log('Fetching CRM brand-source links from API for clientKey:', clientKey, 'brandKey:', brandKey);
    this.crmBrandSourceXrefService.getXrefs(clientKey, brandKey).subscribe({
      next: (data: any[]) => {
        console.log('CRM Brand-Source Xref API Response Received:', data);
        if (Array.isArray(data)) {
          const mappedXrefs = data.map(x => {
            const xrefKey = x.XrefKey ?? x.xrefKey ?? x.id;
            const cKey = x.ClientKey ?? x.clientKey;
            const cName = x.ClientName ?? x.clientName ?? '';
            const bKey = x.BrandKey ?? x.brandKey;
            const bName = x.BrandName ?? x.brandName ?? '';
            const brandGrpKey = x.BrandGrpKey ?? x.brandGrpKey ?? null;
            const sourceKey = x.SourceKey ?? x.sourceKey;
            const sourceName = x.SourceName ?? x.sourceName ?? '';
            const sourceType = x.SourceType ?? x.sourceType ?? '';

            return {
              id: xrefKey,
              xrefKey: xrefKey,
              clientKey: cKey,
              clientName: cName,
              brandKey: bKey,
              brandName: bName,
              brandGrpKey: brandGrpKey,
              sourceKey: sourceKey,
              sourceName: sourceName,
              sourceType: sourceType
            };
          });

          (window as any).DB_BRAND_SOURCE_XREFS_DATA = mappedXrefs;
          if (typeof (window as any).updateBrandSourceXrefsFromApi === 'function') {
            (window as any).updateBrandSourceXrefsFromApi(mappedXrefs);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM brand-source links from API:', err)
    });
  }

  saveCrmBrandSourceXrefApi(xrefPayload: any): void {
    const userEmail = this.username || 'system';
    this.crmBrandSourceXrefService.linkSource(xrefPayload, userEmail).subscribe({
      next: (res) => {
        this.loadBrandSourceXrefs();
      },
      error: (err) => console.error('Error linking CRM data source to brand:', err)
    });
  }

  deleteCrmBrandSourceXrefApi(xrefId: number): void {
    this.crmBrandSourceXrefService.unlinkSource(xrefId).subscribe({
      next: (res) => {
        this.loadBrandSourceXrefs();
      },
      error: (err) => console.error('Error unlinking CRM data source from brand:', err)
    });
  }

  loadCalls(): void {
    console.log('Fetching CRM calls from Outlook/Graph API...');
    this.crmCallService.getCalls().subscribe({
      next: (data: any[]) => {
        console.log('CRM Calls API Response Received:', data);
        if (Array.isArray(data)) {
          const mappedCalls = data.map(c => this.mapCallDto(c));

          (window as any).DB_CALLS_DATA = mappedCalls;
          if (typeof (window as any).updateCallsFromApi === 'function') {
            (window as any).updateCallsFromApi(mappedCalls);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM calls from API:', err)
    });
  }

  private mapCallDto(c: any): any {
    const eventId = c.EventId ?? c.eventId ?? c.id;
    const title = c.Title ?? c.title ?? '';
    const brand = c.Brand ?? c.brand ?? '';
    const partner = c.Partner ?? c.partner ?? '';
    const clientName = c.ClientName ?? c.clientName ?? '';
    const clientKey = c.ClientKey ?? c.clientKey ?? null;
    const start = c.Start ?? c.start ?? '';
    const end = c.End ?? c.end ?? '';
    const tz = c.TimeZone ?? c.timeZone ?? 'IST';
    const freq = c.Frequency ?? c.frequency ?? 'One-time';
    const status = c.Status ?? c.status ?? 'Active';
    const comments = c.Comments ?? c.comments ?? '';
    const joinUrl = c.JoinUrl ?? c.joinUrl ?? '';
    const attendeesRaw = c.Attendees ?? c.attendees ?? [];
    const attendees = Array.isArray(attendeesRaw)
      ? attendeesRaw.map((a: any) => ({ name: a.Name ?? a.name ?? '', email: a.Email ?? a.email ?? '' }))
      : [];

    return {
      id: eventId,
      eventId: eventId,
      title: title,
      brand: brand,
      partner: partner,
      clientName: clientName,
      clientKey: clientKey,
      dt: start,
      end: end,
      tz: tz,
      freq: freq,
      status: status,
      comments: comments,
      joinUrl: joinUrl,
      attendees: attendees
    };
  }

  loadCrmCallUsers(): void {
    this.userService.getCrmUsers().subscribe({
      next: (data: any[]) => {
        if (Array.isArray(data)) {
          const mapped = data.map((u: any) => ({
            name: u.userName ?? u.UserName ?? u.USER_NAME ?? '',
            email: u.userEmail ?? u.UserEmail ?? u.USER_EMAIL ?? ''
          }));
          if (typeof (window as any).updateCrmCallUsersFromApi === 'function') {
            (window as any).updateCrmCallUsersFromApi(mapped);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM users from API:', err)
    });
  }

  saveCrmCallApi(callPayload: any, isEdit: boolean, eventId?: string): void {
    const userEmail = this.username || 'system';
    if (isEdit && eventId) {
      this.crmCallService.updateCall(eventId, callPayload, userEmail).subscribe({
        next: (res) => {
          this.loadCalls();
        },
        error: (err) => console.error('Error updating CRM call:', err)
      });
    } else {
      this.crmCallService.createCall(callPayload, userEmail).subscribe({
        next: (res) => {
          this.loadCalls();
        },
        error: (err) => console.error('Error creating CRM call:', err)
      });
    }
  }

  cancelCrmCallApi(eventId: string): void {
    this.crmCallService.cancelCall(eventId).subscribe({
      next: (res) => {
        this.loadCalls();
      },
      error: (err) => console.error('Error cancelling CRM call:', err)
    });
  }

  loadHolidays(): void {
    this.crmHolidayService.getHolidays().subscribe({
      next: (data: any[]) => {
        if (Array.isArray(data)) {
          const mapped = data.map(h => this.mapHolidayDto(h));
          if (typeof (window as any).updateHolidaysFromApi === 'function') {
            (window as any).updateHolidaysFromApi(mapped);
          }
        }
      },
      error: (err) => console.error('Failed to load CRM holidays from API:', err)
    });
  }

  private mapHolidayDto(h: any): any {
    const id = h.HolidayKey ?? h.holidayKey ?? h.id;
    const name = h.HolidayName ?? h.holidayName ?? '';
    const date = h.HolidayDate ?? h.holidayDate ?? '';
    const observed = h.ObservedDate ?? h.observedDate ?? date;
    const dayLabel = h.DayLabel ?? h.dayLabel ?? '';
    const region = (h.Region ?? h.region ?? 'BOTH').toString().toUpperCase();
    const affectsFiles = h.AffectsFiles ?? h.affectsFiles ?? true;

    return {
      id: id,
      holidayKey: id,
      name: name,
      date: typeof date === 'string' && date.length >= 10 ? date.substring(0, 10) : date,
      observed: typeof observed === 'string' && observed.length >= 10 ? observed.substring(0, 10) : observed,
      day: dayLabel,
      region: region,
      affects_files: affectsFiles
    };
  }

  saveCrmHolidayApi(holidayPayload: any, isEdit: boolean, holidayId?: number): void {
    const userEmail = this.username || 'system';
    if (isEdit && holidayId) {
      this.crmHolidayService.updateHoliday(holidayId, holidayPayload, userEmail).subscribe({
        next: (res) => {
          this.loadHolidays();
        },
        error: (err) => console.error('Error updating CRM holiday:', err)
      });
    } else {
      this.crmHolidayService.createHoliday(holidayPayload, userEmail).subscribe({
        next: (res) => {
          this.loadHolidays();
        },
        error: (err) => console.error('Error creating CRM holiday:', err)
      });
    }
  }

  deleteCrmHolidayApi(holidayId: number): void {
    this.crmHolidayService.deleteHoliday(holidayId).subscribe({
      next: (res) => {
        this.loadHolidays();
      },
      error: (err) => console.error('Error deleting CRM holiday:', err)
    });
  }

  ngOnDestroy(): void {
    const script = document.getElementById('crm-portal-dynamic-script');
    if (script) {
      script.remove();
    }
  }

  goBackToStandardFlow(): void {
    this.router.navigate(['home']);
  }
}
